import React, { useEffect } from 'react';
import mbIcon from '../assets/mb_icon.png';
import './AboutUs.css';

interface AboutUsProps {
  onBack: () => void;
}

const AboutUs: React.FC<AboutUsProps> = ({ onBack }) => {
  useEffect(() => {
    let root: any = null;

    const loadScript = (src: string, id: string) => {
      return new Promise((resolve, reject) => {
        // Check if script already exists
        if (document.getElementById(id)) {
          resolve(true);
          return;
        }

        const script = document.createElement('script');
        script.src = src;
        script.id = id;
        script.async = false; // Load sequentially
        script.onload = () => resolve(true);
        script.onerror = () => reject(new Error(`Failed to load ${src}`));
        document.head.appendChild(script);
      });
    };

    const initMap = async () => {
      try {
        // Load scripts sequentially
        await loadScript('https://cdn.amcharts.com/lib/5/index.js', 'amcharts-core');
        await loadScript('https://cdn.amcharts.com/lib/5/map.js', 'amcharts-map');
        await loadScript('https://cdn.amcharts.com/lib/5/geodata/worldLow.js', 'amcharts-geodata');
        await loadScript('https://cdn.amcharts.com/lib/5/themes/Animated.js', 'amcharts-animated');

        // Small delay to ensure scripts are fully loaded
        await new Promise(resolve => setTimeout(resolve, 100));

        // @ts-ignore - Check if amCharts is available
        if (!window.am5) {
          console.error('amCharts library not loaded');
          return;
        }

        // @ts-ignore
        const am5 = window.am5;
        // @ts-ignore
        const am5map = window.am5map;
        // @ts-ignore
        const am5themes_Animated = window.am5themes_Animated;
        // @ts-ignore
        const am5geodata_worldLow = window.am5geodata_worldLow;

        // Create root element
        root = am5.Root.new("worldMapChart");

        // Set themes
        root.setThemes([am5themes_Animated.new(root)]);

        // Disable amCharts logo (if available)
        if (root._logo) {
          root._logo.dispose();
        }

        // Create the map chart
        const chart = root.container.children.push(
          am5map.MapChart.new(root, {
            panX: "translateX",
            panY: "translateY",
            projection: am5map.geoMercator()
          })
        );

        // Add zoom control
        chart.set("zoomControl", am5map.ZoomControl.new(root, {}));

        // Create polygon series for countries
        const polygonSeries = chart.series.push(
          am5map.MapPolygonSeries.new(root, {
            geoJSON: am5geodata_worldLow,
            exclude: ["AQ"] // Exclude Antarctica
          })
        );

        // Configure polygon appearance
        polygonSeries.mapPolygons.template.setAll({
          tooltipText: "{name}",
          interactive: true,
          fill: am5.color(0xE5E7EB), // Light gray
          stroke: am5.color(0xFFFFFF), // White border
          strokeWidth: 0.5
        });

        // Hover state
        polygonSeries.mapPolygons.template.states.create("hover", {
          fill: am5.color(0xB298DC) // Light purple
        });

        // Active/highlighted state
        polygonSeries.mapPolygons.template.states.create("active", {
          fill: am5.color(0x5C2597) // Dark purple
        });

        // Highlight specific countries after data is loaded
        polygonSeries.events.on("datavalidated", function() {
          const countries = ["IN", "SG", "AE", "SA", "MM", "OM","US","GB","CA","AU"];
          countries.forEach(code => {
            const dataItem = polygonSeries.getDataItemById(code);
            if (dataItem) {
              const polygon = dataItem.get("mapPolygon");
              if (polygon) {
                // Set the fill color directly and make it persistent
                polygon.setAll({
                  fill: am5.color(0x5C2597), // Dark purple
                  fillOpacity: 1
                });

                // Override hover state for highlighted countries to maintain purple
                polygon.states.create("hover", {
                  fill: am5.color(0x7B3DB8), // Slightly lighter purple on hover
                  fillOpacity: 1
                });
              }
            }
          });
        });

        console.log('Map initialized successfully');
      } catch (error) {
        console.error('Error initializing map:', error);
      }
    };

    initMap();

    // Cleanup function
    return () => {
      if (root) {
        root.dispose();
      }
    };
  }, []);
  return (
    <div className="min-vh-100 bg-light">
      {/* Hero Section */}
      <div className="bg-primary-custom text-white animated-bg">
        <div className="container py-5">
          <div className="row align-items-center">
            <div className="col-lg-8">
              <h1 className="mb-4" style={{
                fontSize: '2.5rem',
                fontWeight: '700',
                fontFamily: "'Segoe UI', 'Helvetica Neue', Arial, sans-serif",
                letterSpacing: '0.5px',
                lineHeight: '1.2',
                fontStyle: 'italic'
              }}>
              "Shaping the Future of Learning, Together."
              </h1>
            </div>
            <div className="col-lg-4 text-center">
              <img src={mbIcon} alt="My Learning Board" style={{ width: '200px', height: '200px' }} />
            </div>
          </div>
        </div>
      </div>

      {/* About Section */}
      <div className="container py-5">
        <div className="row mb-5">
          <div className="col-lg-12">
            <div className="card border-0 shadow-sm particles-bg">
              {/* Floating Particles */}
              <div className="particle"></div>
              <div className="particle"></div>
              <div className="particle"></div>
              <div className="particle"></div>
              <div className="particle"></div>
              <div className="card-body p-5 content-layer">
                <h2 className="text-primary-custom mb-4">
                  <i className="bi bi-building me-3"></i>
                  About My Learning Board
                </h2>
                <p className="lead text-muted" style={{ textAlign: 'justify', lineHeight: '1.8' }}>
                  My Learning Board is an intelligent learning and management platform that's transforming education through the power of Artificial Intelligence. Designed for students, educators, and institutions, it brings together innovation, insight, and intelligence to make learning smarter, faster, and more personalized.
                </p>
                <p className="text-muted" style={{ textAlign: 'justify', lineHeight: '1.8' }}>
                  Built by a passionate team of technologists and education experts, My Learning Board blends AI-driven analytics, adaptive learning paths, and automation tools to help institutions save time, enhance engagement, and drive better academic outcomes.
                </p>
                <p className="text-muted" style={{ textAlign: 'justify', lineHeight: '1.8' }}>
                  Our mission is simple — to make quality learning accessible, efficient, and deeply personalized for every learner. With My Learning Board, institutions can easily manage academic processes, empower teachers with real-time insights, and help students unlock their true potential through data-backed recommendations.
                </p>
                <p className="text-muted" style={{ textAlign: 'justify', lineHeight: '1.8' }}>
                  From AI-powered assessments and smart content recommendations to automated progress tracking and intuitive dashboards, My Learning Board provides everything educators and learners need — all in one unified platform.
                </p>
                <p className="text-muted" style={{ textAlign: 'justify', lineHeight: '1.8' }}>
                  Backed by cutting-edge technology and a commitment to continuous innovation, My Learning Board isn't just a tool — it's your partner in shaping the future of learning.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Mission & Vision Cards */}
        <div className="row g-4 mb-5">
          <div className="col-md-6">
            <div className="card border-0 shadow-sm h-100 gradient-wave-bg flowing-lines-bg">
              {/* Flowing Lines */}
              <div className="flowing-line"></div>
              <div className="flowing-line"></div>
              <div className="flowing-line"></div>
              <div className="flowing-line"></div>
              <div className="flowing-line"></div>
              <div className="card-body p-4 content-layer">
                <div className="d-flex align-items-center mb-3">
                  <div className="rounded-circle bg-primary-custom bg-opacity-10 p-3 me-3">
                    <i className="bi bi-rocket-takeoff-fill text-primary-custom fs-3"></i>
                  </div>
                  <h3 className="text-primary-custom mb-0">Our Mission</h3>
                </div>
                <p className="text-muted" style={{ textAlign: 'justify', lineHeight: '1.8' }}>
                  Our mission is to reshape the future of learning with the intelligence of AI. My Learning Board empowers educators with smart insights, automates academic management, and delivers personalized experiences that inspire every learner to reach their full potential.
                </p>
              </div>
            </div>
          </div>

          <div className="col-md-6">
            <div className="card border-0 shadow-sm h-100 gradient-wave-bg circuit-lines-bg">
              {/* Circuit Lines for AI Theme */}
              <div className="circuit-line horizontal"></div>
              <div className="circuit-line horizontal"></div>
              <div className="circuit-line horizontal"></div>
              <div className="circuit-line vertical"></div>
              <div className="circuit-line vertical"></div>
              <div className="circuit-line vertical"></div>
              <div className="card-body p-4 content-layer">
                <div className="d-flex align-items-center mb-3">
                  <div className="rounded-circle bg-primary-custom bg-opacity-10 p-3 me-3 pulse-glow">
                    <i className="bi bi-eye-fill text-primary-custom fs-3"></i>
                  </div>
                  <h3 className="text-primary-custom mb-0">Our Vision</h3>
                </div>
                <p className="text-muted" style={{ textAlign: 'justify', lineHeight: '1.8' }}>
                  Our vision is to create innovative technology solutions to empower educational institutions and digitize education globally. My Learning Board is an enterprise solution built to help school owners and administrators manage their schools with ease.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Locations Section */}
        <div className="row mb-5">
          <div className="col-12">
            <div className="card border-0 shadow-sm mesh-gradient-bg">
              <div className="card-body p-4 content-layer">
                <h3 className="text-primary-custom mb-4">
                  <i className="bi bi-geo-alt-fill me-3"></i>
                  Our Global Presence
                </h3>

                {/* Interactive World Map */}
                <div id="worldMapChart" style={{ width: '100%', height: '600px', marginBottom: '30px' }}></div>

                {/* Country Pills */}
                <div className="row g-3">
                  {['India', 'Singapore', 'UAE', 'Saudi Arabia', 'USA', 'Oman'].map((location) => (
                    <div key={location} className="col-md-4 col-lg-2">
                      <div className="card border-0 text-center p-3 h-100">
                        <i className="bi bi-pin-map-fill text-primary-custom fs-4 mb-2"></i>
                        <h6 className="mb-0 text-primary-custom">{location}</h6>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Contact Information */}
        <div className="row mb-5">
          <div className="col-12">
            <div className="card border-0 shadow-sm">
              <div className="card-body p-4">
                <h3 className="text-primary-custom mb-4">
                  <i className="bi bi-telephone-fill me-3"></i>
                  Contact Information
                </h3>
                <div className="row g-4">
                  <div className="col-md-4">
                    <div className="d-flex align-items-start">
                      <i className="bi bi-envelope-fill text-primary-custom fs-4 me-3"></i>
                      <div>
                        <h6 className="mb-1">Email</h6>
                        <a href="mailto:info@mylearningboard.com" className="text-muted text-decoration-none">
                          info@mylearningboard.com
                        </a>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="d-flex align-items-start">
                      <i className="bi bi-phone-fill text-primary-custom fs-4 me-3"></i>
                      <div>
                        <h6 className="mb-1">Phone</h6>
                        <a href="tel:+918839393463" className="text-muted text-decoration-none">
                          +91 8839393463
                        </a>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="d-flex align-items-start">
                      <i className="bi bi-clock-fill text-primary-custom fs-4 me-3"></i>
                      <div>
                        <h6 className="mb-1">Working Hours</h6>
                        <p className="text-muted mb-0">9AM to 9PM</p>
                        <small className="text-muted">Monday to Saturday</small>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="d-flex align-items-start">
                      <i className="bi bi-building-fill text-primary-custom fs-4 me-3"></i>
                      <div>
                        <h6 className="mb-1">Company</h6>
                        <p className="text-muted mb-0">Omnix Digital Solutions LLP</p>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="d-flex align-items-start">
                      <i className="bi bi-geo-fill text-primary-custom fs-4 me-3"></i>
                      <div>
                        <h6 className="mb-1">Registered Office</h6>
                        <p className="text-muted mb-0">Bengaluru,India</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Download Apps & Social Media */}
        <div className="row g-4 mb-5">
          <div className="col-md-6">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-body p-4">
                <h3 className="text-primary-custom mb-4">
                  <i className="bi bi-phone-fill me-3"></i>
                  Download Our App
                </h3>
                <div className="d-flex gap-3">
                  <a href="#" className="btn btn-dark btn-lg flex-fill">
                    <i className="bi bi-google-play me-2"></i>
                    Play Store
                  </a>
                  <a href="#" className="btn btn-dark btn-lg flex-fill">
                    <i className="bi bi-apple me-2"></i>
                    App Store
                  </a>
                </div>
              </div>
            </div>
          </div>

          <div className="col-md-6">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-body p-4">
                <h3 className="text-primary-custom mb-4">
                  <i className="bi bi-share-fill me-3"></i>
                  Connect With Us
                </h3>
                <div className="d-flex gap-3 flex-wrap">
                  <a href="https://www.facebook.com" target="_blank" rel="noopener noreferrer" className="btn btn-outline-primary rounded-circle" style={{ width: '50px', height: '50px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <i className="bi bi-facebook fs-5"></i>
                  </a>
                  <a href="https://www.instagram.com/" target="_blank" rel="noopener noreferrer" className="btn btn-outline-danger rounded-circle" style={{ width: '50px', height: '50px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <i className="bi bi-instagram fs-5"></i>
                  </a>
                  <a href="https://www.linkedin.com/company/" target="_blank" rel="noopener noreferrer" className="btn btn-outline-primary rounded-circle" style={{ width: '50px', height: '50px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <i className="bi bi-linkedin fs-5"></i>
                  </a>
                  <a href="https://www.youtube.com" target="_blank" rel="noopener noreferrer" className="btn btn-outline-danger rounded-circle" style={{ width: '50px', height: '50px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <i className="bi bi-youtube fs-5"></i>
                  </a>
                  <a href="https://twitter.com" target="_blank" rel="noopener noreferrer" className="btn btn-outline-info rounded-circle" style={{ width: '50px', height: '50px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <i className="bi bi-twitter fs-5"></i>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Call to Action */}
        <div className="row">
          <div className="col-12">
            <div className="card bg-primary-custom text-white border-0 shadow-sm">
              <div className="card-body p-5 text-center">
                <h2 className="mb-3">Ready to Transform Your Institution?</h2>
                <p className="lead mb-4">Join thousands of schools worldwide using My Learning Board</p>
                <div className="d-flex gap-3 justify-content-center">
                  <button className="btn btn-light btn-lg" onClick={onBack}>
                    <i className="bi bi-arrow-left me-2"></i>
                    Back to Home
                  </button>
                  <a href="mailto:info@mylearningboard.com" className="btn btn-outline-light btn-lg">
                    <i className="bi bi-envelope me-2"></i>
                    Contact Sales
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AboutUs;
